import type { SessionListItem } from '@/features/sessions/types';
import { parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { Button } from '@heidi/design-system/button';
import { PopoverContent } from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { AudioLines, Plus, UserRound } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { recentVisitHoverCardMessages as m } from './recent-visit-hover-card.messages';
import { useRecentVisitActions } from './use-recent-visit-actions';

interface RecentVisitHoverCardProps {
  session: SessionListItem;
  /** Resolved patient identity, or falsy when the visit has none. */
  patientName: string | null | undefined;
  /** Session name as the row renders it, so the card cannot disagree with it. */
  sessionLabel: string;
  /** Hand focus back to the row's button when the card is dismissed by keyboard. */
  onReturnFocus: () => void;
  /** Close the card — hover-out is the row's job, keyboard exit is the card's. */
  onClose: () => void;
  /** Keeps the card up while the pointer crosses the gap from the row into it. */
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** Resolved from direction by the host — Radix's `side` is physical. */
  side: 'left' | 'right';
}

/**
 * Longer than the app-wide 350ms, and paired with `skipDelayDuration={0}`
 * below. The card is itself a hover surface that took 400ms to appear, and its
 * three actions are adjacent full-width targets — so reaching the third means
 * crossing the other two. On the app's settings a tooltip fired on the way
 * past, and worse, Radix's skip window (300ms by default) opens every tooltip
 * after the first with NO delay at all, which is what read as too fast.
 */
const ACTION_TOOLTIP_DELAY_MS = 600;

interface CardActionProps {
  /** Serves twice, on purpose: the button's accessible name AND the tooltip's
   *  text. One string means what a sighted user reads and what a screen reader
   *  announces cannot drift apart (WCAG 2.5.3, Label in Name). */
  label: string;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}

/**
 * One action in the card's toolbar: an icon that names itself underneath after
 * a pause.
 *
 * Three glyphs with no labels are only legible once you have learned them, and
 * the card is too small to carry the words inline. Timing and hover behaviour
 * come from the card's own `TooltipProvider` rather than the app-wide one — see
 * {@link ACTION_TOOLTIP_DELAY_MS} for why this cluster needs its own.
 *
 * `aria-describedby={undefined}` suppresses the one Radix puts on an open
 * trigger. It would point at this same string, so a screen reader announced
 * "Add to chat, button, Add to chat" — the name twice, once as a description
 * that adds nothing. The tooltip is a visual aid for a glyph a sighted user
 * cannot read; a screen reader user already has the name.
 */
const CardAction = ({ label, onClick, testId, children }: CardActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="md"
        iconOnly
        className="w-full"
        aria-label={label}
        aria-describedby={undefined}
        onClick={onClick}
        data-testid={testId}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom" data-testid="sidebar-recent-visit-hover-tooltip">
      {label}
    </TooltipContent>
  </Tooltip>
);

/**
 * The Recents row preview: identity, when the visit happened, and the three
 * things a clinician wants to do with it without leaving the sidebar.
 *
 * ## Why a Popover and not a HoverCard
 *
 * `@heidi/design-system/hover-card` looks like the right primitive and is not:
 * Radix's HoverCard content runs `getTabbableNodes(content).forEach(n =>
 * n.setAttribute('tabindex', '-1'))` on every render, so any button inside it
 * is keyboard-unreachable by construction. The card carries actions, so it has
 * to be a Popover — opened on hover and on row focus, and never stealing focus
 * (`onOpenAutoFocus` is prevented).
 *
 * ## Layout
 *
 * One flush-left text column — a primary line and however many secondary lines
 * the visit has — over a row of actions. No leading glyph: an avatar and a
 * calendar in the same slot were two different promises about what the card is
 * about, and the indent they forced bought nothing back. Primary and secondary
 * share a size and separate on colour. The card holds ONE fixed width, so a
 * short date-only card reads as the same object as a patient one and the card
 * cannot resize under the pointer as it moves between rows.
 *
 * It is positioned off the NAV PANEL's inline-end edge rather than the row's,
 * via a virtual anchor in `RecentVisitHoverCardHost`; anchoring to the row put
 * the card's leading edge back inside the navigation, because a row is inset
 * by the panel's own padding. `side` is passed in rather than hard-coded
 * because Radix's is physical and does not flip for RTL.
 *
 * ## Keyboard
 *
 * The actions are a horizontal `toolbar`: one tab stop, arrow keys between the
 * buttons, which is what WAI-ARIA prescribes for a button group. The row owns
 * entry (Tab from the focused row moves in here); this component owns exit —
 * Escape, or Tab in either direction, closes and returns focus to the row, so
 * the user's next Tab continues down the list from where they were.
 */
export const RecentVisitHoverCard = forwardRef<HTMLDivElement, RecentVisitHoverCardProps>(
  function RecentVisitHoverCard(
    {
      session,
      patientName,
      sessionLabel,
      onReturnFocus,
      onClose,
      onMouseEnter,
      onMouseLeave,
      side,
    },
    ref
  ) {
    const intl = useIntl();
    const toolbarRef = useRef<HTMLDivElement>(null);
    const { addToChat, openPatient, openSession } = useRecentVisitActions(
      session,
      sessionLabel
    );

    // The same parse the Recents date headers use (`getRecentItemBucketDate`),
    // so a card cannot read "25 Sep" under a "Today" header.
    const visitDate = parseTimeStampOrNull(session.created_at, true);

    // A name alone is not enough to route to a patient: `resolvePatientName`
    // falls back to the session's free-text patient, which has no profile behind
    // it and would open an empty canvas.
    const patientProfileId = session.patient_profile_id;
    const canOpenPatient = Boolean(patientProfileId && patientName);

    const dateLine = visitDate
      ? intl.formatDate(visitDate, { dateStyle: 'medium' })
      : null;
    const timeLine = visitDate
      ? intl.formatTime(visitDate, { timeStyle: 'short' })
      : null;

    // One shape, three sources for its top line. A patient leads when there is
    // one; otherwise the date is promoted into that slot rather than sitting in
    // the card as undersized orphan text. The session label is the last resort
    // for a visit with neither, which beats a card that is only buttons.
    //
    // Under a patient the whole timestamp is one line, via the locale's own
    // combined form rather than a hand-joined one — en-US puts a comma in that
    // an ICU list would not, and other locales order the two parts differently.
    // When the date has been promoted it keeps the time on a line of its own,
    // so both variants settle at the same two-line height.
    const { primary, secondary } = patientName
      ? {
          primary: patientName,
          secondary: visitDate
            ? [intl.formatDate(visitDate, { dateStyle: 'medium', timeStyle: 'short' })]
            : [],
        }
      : dateLine
        ? { primary: dateLine, secondary: timeLine ? [timeLine] : [] }
        : { primary: sessionLabel, secondary: [] };

    const dismissToRow = useCallback(() => {
      onClose();
      onReturnFocus();
    }, [onClose, onReturnFocus]);

    const handleToolbarKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab' || event.key === 'Escape') {
          event.preventDefault();
          dismissToRow();
          return;
        }
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const buttons = Array.from(
          toolbarRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []
        );
        const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
        if (current === -1) return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        buttons[(current + delta + buttons.length) % buttons.length]?.focus();
      },
      [dismissToRow]
    );

    const runAction = useCallback(
      (action: () => void) => () => {
        onClose();
        action();
      },
      [onClose]
    );

    return (
      <PopoverContent
        ref={ref}
        side={side}
        align="center"
        // Measured from the nav panel's edge, not the row's — see the anchor
        // in `RecentVisitHoverCardHost`. A real gutter rather than a hairline,
        // so the card reads as sitting on the content side of the divide.
        sideOffset={8}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        // Hover must never move the caret. The row owns focus; Tab from it is
        // what brings the user in here.
        onOpenAutoFocus={(event) => event.preventDefault()}
        // The fallback for Escape with the card focused but no action focused.
        // The toolbar handles its own, and has to: an action's tooltip is a
        // dismissable layer above this one while it is open, so Radix routes
        // Escape there and never calls this — which would cost a keyboard user
        // two presses to leave one card.
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          dismissToRow();
        }}
        // ONE fixed width, not shrink-to-fit. Sizing to content meant the card
        // resized under the pointer as it moved between rows, which is the one
        // thing a preview that stays open must not do.
        //
        // 192px is set by the timestamp line, the only content whose length is
        // not up to the user: "Aug 23, 2026, 10:17 AM" measures ~137px at p2,
        // so the 160px of content this allows leaves it room for a longer
        // locale format. A name past that truncates, which is the intended
        // trade.
        className="w-48 gap-3 p-4"
        aria-label={intl.formatMessage(m.cardLabel, { sessionName: sessionLabel })}
        data-testid="sidebar-recent-visit-hover-card"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <TypographyP2 className="truncate text-text-primary">{primary}</TypographyP2>
          {secondary.map((line) => (
            <TypographyP2 key={line} className="truncate text-text-secondary">
              {line}
            </TypographyP2>
          ))}
        </div>

        {/* Its own provider, so the card carries its whole tooltip contract
            rather than inheriting timing from an ancestor. `skipDelayDuration`
            is off: within three adjacent targets, a skip window means every
            tooltip after the first appears instantly. `disableHoverableContent`
            because the label is never a target — Radix otherwise keeps a grace
            area onto the content, and `side="bottom"` puts that content below
            the card, outside the popover, so following it would take the whole
            card down. */}
        <TooltipProvider
          delayDuration={ACTION_TOOLTIP_DELAY_MS}
          skipDelayDuration={0}
          disableHoverableContent
        >
          <div
            ref={toolbarRef}
            role="toolbar"
            aria-label={intl.formatMessage(m.actions)}
            aria-orientation="horizontal"
            // Three equal slots, each action filling its own (`w-full` below).
            // The hover pill is then the slot rather than a 36px square inside
            // it, so moving between two actions reads as one surface handing
            // over to the next instead of two chips with a void between them.
            //
            // Filling the slot is also what sets each glyph's position: centred
            // in its third rather than pinned to an edge, which nudges Add to
            // chat off the text's left margin and evens out the spacing.
            //
            // Three columns whatever the count. Dropping the patient empties the
            // last slot and moves Open session into the middle, beside the plus,
            // rather than stranding it at the far edge.
            className="grid grid-cols-3 items-center gap-1"
            onKeyDown={handleToolbarKeyDown}
          >
            <CardAction
              label={intl.formatMessage(m.addToChat)}
              onClick={runAction(addToChat)}
              testId="sidebar-recent-visit-hover-add-to-chat"
            >
              <Plus />
            </CardAction>
            {canOpenPatient ? (
              <CardAction
                label={intl.formatMessage(m.viewPatient)}
                onClick={runAction(openPatient)}
                testId="sidebar-recent-visit-hover-view-patient"
              >
                <UserRound />
              </CardAction>
            ) : null}
            <CardAction
              label={intl.formatMessage(m.openSession)}
              onClick={runAction(openSession)}
              testId="sidebar-recent-visit-hover-open-session"
            >
              {/* The Scribe mark the rest of the app already uses for a
                session — the Scribe nav row, the create menu, and this
                row's own leading marker when the visit has no patient. */}
              <AudioLines />
            </CardAction>
          </div>
        </TooltipProvider>
      </PopoverContent>
    );
  }
);
